import { useState } from 'react';
import { Outlet, createFileRoute, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import {
  HomeFilterMenuRail,
  SportsContextSwitcher,
  useSportsFavorites,
  type HomeFilterId,
} from '@/ui';
import { sportChangeTarget } from '@/shell/sports-menu-utils';

/**
 * Sports area layout: keeps the mobile SportsContextSwitcher mounted while
 * sport / category / tournament child routes swap only the content below.
 * Event details hides the switcher so the page can use back + breadcrumb chrome.
 */
export const Route = createFileRoute('/sports')({
  component: SportsLayout,
});

function SportsLayout() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { hasPrematchFavorites } = useSportsFavorites();
  const sport = typeof params.sport === 'string' ? params.sport : undefined;
  const categoryFromRoute = typeof params.category === 'string' ? params.category : undefined;
  const tournamentFromRoute =
    typeof params.tournament === 'string' ? params.tournament : undefined;
  const eventIdFromRoute = typeof params.eventId === 'string' ? params.eventId : undefined;
  const onLastMinute = pathname.includes('/lastminute');
  const onTopEvents = pathname.includes('/topevents');
  const onToday = pathname.includes('/today');
  const onFavorites = pathname.includes('/favorites');

  const [localCategory, setLocalCategory] = useState<string | null>(null);
  const [categorySport, setCategorySport] = useState(sport);
  const [homeFilter, setHomeFilter] = useState<HomeFilterId | null>(null);

  if (sport !== categorySport) {
    setCategorySport(sport);
    setLocalCategory(null);
  }

  // Route category wins once the user has drilled into category/tournament URLs.
  const selectedCategorySlug = categoryFromRoute ?? localCategory ?? undefined;
  // Top Events / Last Minute / Today / Favourites selection comes only from the route — landing has none.
  const selectedHomeFilter: HomeFilterId | null = onLastMinute
    ? 'last-minute'
    : onTopEvents
      ? 'top-events'
      : onToday
        ? 'today'
        : onFavorites
          ? 'favorites'
          : homeFilter;

  return (
    <div className="flex flex-col gap-4">
      {sport && !eventIdFromRoute && (
        <>
          <HomeFilterMenuRail
            value={selectedHomeFilter}
            showFavorites={hasPrematchFavorites}
            onValueChange={(id) => {
              if (id === 'live') {
                void navigate({
                  to: '/live',
                  search: { sport, tournament: undefined, media: undefined },
                });
                return;
              }

              if (id === 'last-minute') {
                void navigate({ to: '/sports/$sport/lastminute', params: { sport } });
                return;
              }

              if (id === 'top-events') {
                void navigate({ to: '/sports/$sport/topevents', params: { sport } });
                return;
              }

              if (id === 'today') {
                void navigate({ to: '/sports/$sport/today', params: { sport } });
                return;
              }

              if (id === 'favorites') {
                void navigate({ to: '/sports/$sport/favorites', params: { sport } });
                return;
              }

              setHomeFilter(id);
              if (onLastMinute || onTopEvents || onToday || onFavorites) {
                void navigate({ to: '/sports/$sport', params: { sport } });
              }
            }}
          />

          <div className="-mx-4 lg:hidden">
            <SportsContextSwitcher
              sportSlug={sport}
              selectedCategorySlug={selectedCategorySlug}
              selectedTournamentSlug={tournamentFromRoute}
              onSportSelect={(slug) => {
                setLocalCategory(null);
                void navigate(sportChangeTarget(pathname, slug));
              }}
              onCategorySelect={(categorySlug) => {
                if (!categorySlug) {
                  setLocalCategory(null);
                  if (categoryFromRoute) {
                    void navigate({ to: '/sports/$sport', params: { sport } });
                  }
                  return;
                }

                setLocalCategory(categorySlug);
                void navigate({
                  to: '/sports/$sport/$category',
                  params: { sport, category: categorySlug },
                });
              }}
            />
          </div>
        </>
      )}

      <Outlet />
    </div>
  );
}
